import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Divider, Form, Radio, Row, Select, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import BreadCrumb from '../breadcrumbs';
import FormTitleHeader from '../Header/FormTitleHeader';
import request from '../../helper/request';
import { useDispatch, useSelector } from 'react-redux';
import { setMessage } from '../../redux/slice';
import './JobOrderPaymentPage.scss';
import { doGetJobOrder } from '../../modules/jobOrders/stores/thunk';
import * as companyApi from '../../modules/company/api';

// Stripe configuration - moved outside of component to ensure proper initialization
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const JobOrderPaymentForm = () => {
  const navigate = useNavigate();
  const { jobOrderId } = useParams();
  const [isFetching, setIsFetching] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const jobOrder = useSelector((state) => state.jobOrder.jobOrderDetail);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState();
  const [companyCards, setCompanyCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState();
  const searchTimerRef = useRef();

  const breadCrumbs = [{ text: 'JOB ORDERS', url: '/job-orders' }, { text: 'PAYMENT' }];

  useEffect(() => {
    dispatch(doGetJobOrder(jobOrderId));
  }, [jobOrderId]);

  const handlePaymentConfirmation = async () => {
    if (!stripe || !elements) {
      dispatch(setMessage('Stripe is not initialized. Please try again.'));
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      dispatch(setMessage('Card details are missing. Please enter your card information.'));
      return;
    }

    setIsFetching(true);

    try {
      // Create payment method with more detailed error handling
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      console.log('Stripe Payment Method Creation:', { error, paymentMethod });

      if (error) {
        console.error('Stripe Payment Method Error:', error);
        throw new Error(error.message || 'Payment method creation failed');
      }

      if (!paymentMethod || !paymentMethod.id) {
        throw new Error('Invalid payment method created');
      }

      // Send payment to backend
      const response = await request(`/job-orders/${jobOrderId}/pay`, {
        method: 'POST',
        data: {
          paymentMethodId: paymentMethod.id,
        },
      });

      setIsFetching(false);
      navigate('/job-orders');
      dispatch(setMessage(response.message));
    } catch (error) {
      console.error('Payment Confirmation Error:', error);
      setIsFetching(false);
      dispatch(setMessage(error.message || 'Unable to process payment. Please try again.'));
    }
  };

  const fetchCompanies = async (q) => {
    setCompaniesLoading(true);
    const { response, error } = await companyApi.getCompanies({ take: 50, q: q || undefined });
    setCompaniesLoading(false);
    if (!error && response?.items) {
      setCompanies(response.items);
    }
  };

  const handleCompanySearch = (value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchCompanies(value?.trim()), 300);
  };

  const handleCompanyChange = async (companyId) => {
    setSelectedCompanyId(companyId);
    setCompanyCards([]);
    setSelectedCardId(undefined);
    if (!companyId) return;

    setCardsLoading(true);
    const { response, error } = await companyApi.listPaymentMethods(companyId);
    setCardsLoading(false);

    if (error) {
      dispatch(setMessage(error.message || 'Unable to load company cards.'));
      return;
    }

    const cards = response?.paymentMethods || [];
    setCompanyCards(cards);
    if (cards.length === 1) {
      setSelectedCardId(cards[0].id);
    }
  };

  const handleCompanyPayment = async () => {
    if (!selectedCompanyId) {
      dispatch(setMessage('Please select a company.'));
      return;
    }
    if (!selectedCardId) {
      dispatch(setMessage('Please select a company card.'));
      return;
    }

    setIsFetching(true);
    const { response, error } = await request(`/job-orders/${jobOrderId}/pay-with-company`, {
      method: 'POST',
      data: { companyId: selectedCompanyId, paymentMethodId: selectedCardId },
    });
    setIsFetching(false);

    if (error) {
      dispatch(setMessage(error.message || 'Unable to process company payment. Please try again.'));
      return;
    }

    navigate('/job-orders');
    dispatch(setMessage(response?.message || 'Payment successful'));
  };

  const cardBrandLabel = (brand) => {
    const map = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express' };
    return map[brand] || brand;
  };

  // Special Deliveries can be paid with a saved company card (back office).
  useEffect(() => {
    if (jobOrder?.isSpecialDelivery) {
      fetchCompanies();
    }
  }, [jobOrder?.isSpecialDelivery]);

  return (
    <Spin spinning={isFetching}>
      <div className="Content JobOrderPaymentPage">
        <BreadCrumb breadCrumbs={breadCrumbs} />
        <div className="FormContent">
          <FormTitleHeader title="Payment" />
          <Divider />

          <div className="header-step">
            <div className="content">
              <p>Use credit or debit card to pay for your job posting</p>
            </div>
            <div className="cost">
              <div className="cost-container">
                <b>Base Cost</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>${jobOrder?.amount ? parseFloat(Number(jobOrder.amount).toFixed(2)) : '0'}</h4>
                  </div>
                </div>
              </div>
              <div className="cost-container">
                <b>GST </b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>{(jobOrder?.gstRate || 0) * 100}%</h4>
                  </div>
                </div>
              </div>
              <div className="cost-container">
                <b>{jobOrder?.isSpecialDelivery ? 'Driver Rate' : 'Driver Payment (62%)'}</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>
                      $
                      {jobOrder?.isSpecialDelivery
                        ? parseFloat(Number(jobOrder?.driverRate || 0).toFixed(2))
                        : jobOrder?.amount
                          ? parseFloat((Number(jobOrder.amount) * (1 + (jobOrder?.gstRate || 0)) * 0.62).toFixed(2))
                          : '0'}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="cost-container">
                <b>Total Cost</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>
                      $
                      {jobOrder?.amount
                        ? parseFloat((Number(jobOrder.amount) * (1 + (jobOrder?.gstRate || 0))).toFixed(2))
                        : '0'}
                    </h4>
                  </div>
                </div>
                {/* <Button size="large" type="link">
                  View Cost Breakdown
                  <ReactSVG className="icon" src="/icons/list_alt.svg" />
                </Button> */}
              </div>
            </div>
          </div>
          <Divider />

          <Form form={form} layout="vertical" onFinish={handlePaymentConfirmation}>
            <div className="payment-container">
              <Row gutter={[20, 24]}>
                <Col span={12}>
                  <div className="ant-form-item cards">
                    <Form.Item
                      name="cardDetails"
                      label="Payment Method"
                      rules={[{ required: true, message: 'Please enter card details' }]}
                    >
                      <CardElement
                        options={{
                          hidePostalCode: true,
                          classes: {
                            base: 'StripeCardElement',
                            complete: 'StripeCardElement--complete',
                            empty: 'StripeCardElement--empty',
                            focus: 'StripeCardElement--focus',
                            invalid: 'StripeCardElement--invalid',
                          },
                        }}
                        className="stripe-card-element"
                      />
                    </Form.Item>
                  </div>
                </Col>
                <Col span={12}></Col>
              </Row>
            </div>

            {jobOrder?.isSpecialDelivery && (
              <div className="payment-container" style={{ paddingTop: 8 }}>
                <Divider plain>Or pay with a company card</Divider>
                <Row gutter={[20, 24]}>
                  <Col span={12}>
                    <div className="ant-form-item">
                      <label>Company</label>
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select company"
                        style={{ width: '100%' }}
                        loading={companiesLoading}
                        value={selectedCompanyId}
                        onChange={handleCompanyChange}
                        onSearch={handleCompanySearch}
                        filterOption={false}
                        options={companies.map((company) => ({
                          value: company.id,
                          label: company.ownerClient?.email
                            ? `${company.name} (${company.ownerClient.email})`
                            : company.name,
                        }))}
                      />
                    </div>
                    {selectedCompanyId && (
                      <div style={{ marginTop: 12 }}>
                        {cardsLoading ? (
                          <Spin size="small" />
                        ) : companyCards.length > 0 ? (
                          <>
                            <Radio.Group value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
                              {companyCards.map((card) => (
                                <Radio key={card.id} value={card.id}>
                                  {cardBrandLabel(card.brand)} &bull;&bull;&bull;&bull; {card.last4} (exp{' '}
                                  {card.expMonth}/{card.expYear})
                                </Radio>
                              ))}
                            </Radio.Group>
                            <div style={{ marginTop: 12 }}>
                              <Button
                                size="large"
                                className="ButtonPrimary"
                                type="primary"
                                htmlType="button"
                                disabled={!selectedCardId}
                                onClick={handleCompanyPayment}
                              >
                                Pay with company card
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div style={{ color: '#999', fontSize: 13 }}>
                            No saved card for this company. Add one in the Companies page first.
                          </div>
                        )}
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            )}

            <div className="FormFooterNewJobOrder">
              <div className="left">
                <Button size="large" onClick={() => navigate(`/job-orders/${jobOrderId}`)}>
                  Back
                </Button>
              </div>
              <div className="right">
                <Button size="large" className="CreateButton ButtonPrimary" type="primary" htmlType="submit">
                  Confirm to Pay
                  <ReactSVG className="icon" src="/icons/done.svg" />
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Spin>
  );
};

const JobOrderPaymentPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <JobOrderPaymentForm />
    </Elements>
  );
};

export default JobOrderPaymentPage;
